import { useState, useEffect } from 'react';
import { Download, Eye, EyeOff, File, FileSpreadsheet, FileText, Image as ImageIcon, Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';
import type { Attachment } from '@/types/email';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DOCX_MIME_TYPE, SPREADSHEET_MIME_TYPES } from '../hooks/useInlineAttachments';
import { formatFileSize, renderAttachmentPreview } from '../utils/attachmentPreview';

interface AttachmentListProps {
  attachments: Attachment[];
  previewing: Record<string, string>;
  downloading: Record<string, boolean>;
  onTogglePreview: (attachment: Attachment) => void | Promise<void>;
  onDownload: (attachment: Attachment) => Promise<void>;
  isPreviewable: (mimeType: string) => boolean;
  align?: 'start' | 'end';
}

const MIN_IMAGES_FOR_GRID = 3;

export function AttachmentList({
  attachments,
  previewing,
  downloading,
  onTogglePreview,
  onDownload,
  isPreviewable,
  align = 'start',
}: AttachmentListProps) {
  if (attachments.length === 0) {
    return null;
  }

  // Separate images from other attachments
  const imageAttachments = attachments.filter((att) => att.mimeType?.startsWith('image/'));
  const otherAttachments = attachments.filter((att) => !att.mimeType?.startsWith('image/'));

  const useGridLayout = imageAttachments.length >= MIN_IMAGES_FOR_GRID;

  return (
    <section className={cn('space-y-3 text-sm font-semibold', align === 'end' && 'items-end')}>
      {/* Image Grid Layout */}
      {useGridLayout && imageAttachments.length > 0 && (
        <ImageGrid
          attachments={imageAttachments}
          previewing={previewing}
          downloading={downloading}
          onTogglePreview={onTogglePreview}
          onDownload={onDownload}
        />
      )}

      {/* Traditional List Layout for non-grid images and other files */}
      <div className="space-y-3">
        {(useGridLayout ? otherAttachments : attachments).map((attachment) => {
          const downloadingAttachment = downloading[attachment.id];
          const previewUrl = previewing[attachment.id];
          // Resolve the preview node up front: if the attachment's mimeType has
          // no renderer, don't show an empty preview container.
          const previewNode = previewUrl ? renderAttachmentPreview(attachment, previewUrl) : null;
          const showingPreview = previewUrl !== undefined;
          const previewable = isPreviewable(attachment.mimeType ?? '');
          const AttachmentIcon = selectAttachmentIcon(attachment.mimeType ?? '');

          return (
            <div key={attachment.id} className="space-y-2">
              <div
                className={cn(
                  'border border-primary/60 bg-primary/5 shadow-sm flex items-center justify-between rounded-lg px-3 py-2',
                  align === 'end' && 'flex-row-reverse',
                )}
              >
                <div className="flex items-center gap-2">
                  <AttachmentIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium truncate max-w-[calc(100vw-200px)] sm:max-w-[calc(100vw-250px)]">
                      {formatAttachmentName(attachment.filename)}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {previewable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onTogglePreview(attachment)}
                      className="gap-2"
                    >
                      {showingPreview && previewUrl ? (
                        <>
                          <EyeOff className="h-4 w-4 sm:hidden" />
                          <span className="hidden sm:inline">Hide</span>
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 sm:hidden" />
                          <span className="hidden sm:inline">Preview</span>
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    disabled={downloadingAttachment}
                    onClick={() => onDownload(attachment)}
                  >
                    {downloadingAttachment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">
                      {downloadingAttachment ? 'Downloading…' : 'Download'}
                    </span>
                  </Button>
                </div>
              </div>
              {previewNode && (
                <div className="glass rounded-lg p-4">
                  {previewNode}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

interface ImageGridProps {
  attachments: Attachment[];
  previewing: Record<string, string>;
  downloading: Record<string, boolean>;
  onTogglePreview: (attachment: Attachment) => void | Promise<void>;
  onDownload: (attachment: Attachment) => Promise<void>;
}

function ImageGrid({
  attachments,
  previewing,
  downloading,
  onTogglePreview,
  onDownload,
}: ImageGridProps) {
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate max visible images for 2 rows
  // Mobile: 2 cols × 2 rows = 4, sm: 3 cols × 2 rows = 6, md+: 4 cols × 2 rows = 8
  // Use the maximum (8) to ensure we show 2 rows on all screen sizes
  const MAX_VISIBLE_IMAGES = 8;
  const visibleAttachments = isExpanded ? attachments : attachments.slice(0, MAX_VISIBLE_IMAGES);
  const hiddenCount = attachments.length - MAX_VISIBLE_IMAGES;
  const hasMoreImages = hiddenCount > 0;

  // Auto-load previews for visible images in the grid
  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    visibleAttachments.forEach((attachment) => {
      if (!previewing[attachment.id] && !loadedImages.has(attachment.id) && !failedImages.has(attachment.id)) {
        setLoadedImages((prev) => new Set(prev).add(attachment.id));
        const result = onTogglePreview(attachment);
        if (result && typeof (result as Promise<void>).catch === 'function') {
          (result as Promise<void>).catch(() => {
            setFailedImages((prev) => new Set(prev).add(attachment.id));
          });
        }
        // Timeout fallback: if preview hasn't loaded after 30s, mark as failed
        const attachmentId = attachment.id;
        const timeout = setTimeout(() => {
          // Only mark failed if still no preview URL
          setFailedImages((prev) => {
            if (prev.has(attachmentId)) return prev;
            return new Set(prev).add(attachmentId);
          });
        }, 30000);
        timeouts.push(timeout);
      }
    });
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [visibleAttachments, previewing, onTogglePreview, loadedImages, failedImages]);

  const renderImageItem = (attachment: Attachment) => {
    const previewUrl = previewing[attachment.id];
    const isDownloading = downloading[attachment.id];

    return (
      <div
        key={attachment.id}
        className="group relative aspect-square overflow-hidden rounded-lg border border-border/50 bg-muted/30 cursor-pointer"
        onClick={() => previewUrl && setExpandedImage(attachment.id)}
      >
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt={attachment.filename}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none">
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <p className="text-xs font-medium text-white truncate">
                  {formatAttachmentName(attachment.filename)}
                </p>
              </div>
            </div>
            <div className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                size="sm"
                variant="secondary"
                className="h-7 w-7 p-0 bg-white/90 hover:bg-white"
                disabled={isDownloading}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(attachment);
                }}
              >
                {isDownloading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className="h-3 w-3" />
                )}
              </Button>
            </div>
          </>
        ) : failedImages.has(attachment.id) ? (
          <div
            className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              setFailedImages((prev) => {
                const next = new Set(prev);
                next.delete(attachment.id);
                return next;
              });
              setLoadedImages((prev) => {
                const next = new Set(prev);
                next.delete(attachment.id);
                return next;
              });
            }}
          >
            <ImageIcon className="h-6 w-6" />
            <span className="text-[10px]">Tap to retry</span>
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="rounded-lg border border-primary/60 bg-primary/5 p-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold text-muted-foreground">
            {attachments.length} Image{attachments.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {visibleAttachments.map(renderImageItem)}
        </div>
        {hasMoreImages && (
          <div className="mt-3 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="gap-2 text-xs"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show {hiddenCount} More
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div
          // Top padding keeps the image — and the close button 3rem above it —
          // below the notch inset even when the image hits its max height.
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 pt-[calc(env(safe-area-inset-top,0px)+3.5rem)]"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="secondary"
              className="absolute -top-12 right-0 gap-2"
              onClick={() => setExpandedImage(null)}
            >
              <X className="h-4 w-4" />
              Close
            </Button>
            {previewing[expandedImage] && (
              <img
                src={previewing[expandedImage]}
                alt={attachments.find((a) => a.id === expandedImage)?.filename}
                className="max-h-[calc(100dvh-env(safe-area-inset-top,0px)-8rem)] max-w-[90vw] rounded-lg object-contain"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function selectAttachmentIcon(mimeType: string) {
  const normalized = mimeType.toLowerCase();
  if (normalized.startsWith('image/')) {
    return ImageIcon;
  }
  if (SPREADSHEET_MIME_TYPES.has(normalized)) {
    return FileSpreadsheet;
  }
  if (normalized === 'application/pdf' || normalized === DOCX_MIME_TYPE || normalized.startsWith('text/')) {
    return FileText;
  }
  return File;
}

function formatAttachmentName(filename: string): string {
  if (!filename) {
    return 'Attachment';
  }
  // Clean display for generated filenames like "attachment-1.jpg" -> "Attachment 1 (jpg)"
  const placeholderMatch = filename.match(/^attachment-(\d+)(?:\.([a-z0-9]+))?$/i);
  if (placeholderMatch) {
    const num = placeholderMatch[1];
    const ext = placeholderMatch[2];
    return ext ? `Attachment ${num} (${ext.toLowerCase()})` : `Attachment ${num}`;
  }
  return filename;
}
